import { useContext, useRef, type ReactNode } from "react";
import { useLiveDraft } from "./useLiveDraft";
import { Button } from "../ui/Button";
import { DateTimeField } from "../ui/DateTimeField";
import { toBig, type Money } from "../../lib/big";
import { Field, FieldNote, Form, NumInput } from "../ui/Field";
import { Notice } from "../ui/Notice";
import { Panel, SectionTitle } from "../ui/Panel";
import { Tag } from "../ui/Tag";
import { BCoins, BScaleContext } from "./BCoins";
import { GNumInput } from "./GNumInput";
import { HelpVideo } from "../ui/HelpVideo";
import type { HelpVideoId } from "../../lib/helpVideos";
import { useT, type MsgKey, type Translate } from "../../lib/i18n";

export interface ParamSpec<T> {
  key: keyof T & string;
  /** 文案走 i18n key，不写死语言 */
  label: MsgKey;
  unit?: MsgKey;
  hint?: MsgKey;
  /**
   * 字段右侧的说明。传函数就能拿当前草稿算派生值——
   * 「空仓存满约 3.3 小时」这类要随输入实时变，静态文案没用。
   * 函数第二个参数是 t，方便在派生文案里插值。
   */
  note?: MsgKey | ((draft: T, t: Translate) => ReactNode);
  /** 该值是否合法；不合法时禁用应用按钮 */
  valid?: (v: number | null) => boolean;
  /**
   * 金币量级的字段（首关收入 B、满级替代金币）：没有上限，服务端读出是十进制字符串。
   * 打开后用精确整数输入，值以 Money 进出（lib/big.ts），合法性改看 validExact（缺省 = 填了且不为负）
   */
  exact?: boolean;
  validExact?: (v: bigint | null) => boolean;
  /**
   * `datetime` 的值是 UTC 毫秒，按租户时区编辑与显示。
   * `bool` 存的仍是数字 0 / 1（cfg_main_common 全是数字列），只是编成勾选框——
   * 三态开关做成数字输入框，运营迟早会填个 2 进去。
   */
  type?: "number" | "datetime" | "bool";
  /** 参数说明视频（lib/helpVideos.ts 的 id）。给了，标签旁出一个播放图标 */
  video?: HelpVideoId;
}

export interface ParamGroup<T> {
  title: MsgKey;
  /** 标题右侧的标签，如「影响全部关卡」 */
  tag?: { tone: "warning" | "danger" | "info" | "primary" | "success"; text: MsgKey };
  /** 醒目警告条：影响面大的分组必须用它，不能降级成灰色小字 */
  notice?: { tone: "warning" | "danger" | "info" | "plain"; text: MsgKey };
  /** 组尾说明，缩进对齐到控件列 */
  footer?: MsgKey;
  fields: ParamSpec<T>[];
  /**
   * 纯说明面板：没有可改字段，只有几条规则（如「新手保护」）。
   * 写死在代码里而不是配置项，是因为它们本来就是代码里写死的规则——
   * 摆成输入框会让人以为能改。
   */
  bullets?: MsgKey[];
}

/*
 * 默认规则 = 发布闸的口径：填了、且不为负。0 是合法值（「每日广告上限 0」= 不开放广告，config-validate 的用例钉着）；
 * 以前默认「必须填正数」，运营根本填不进 0，而且不合规的值只留在本地、不存——刷新就丢、别人看不到（10-10 从零建代理实测）
 */
const NON_NEG = (v: number | null) => v != null && v >= 0;
const EXACT_NON_NEG = (v: bigint | null) => v != null && v >= 0n;
/** 这一格合不合法：精确字段按 bigint 判 */
const validOf = <T,>(f: ParamSpec<T>, v: unknown) =>
  f.exact ? (f.validExact ?? EXACT_NON_NEG)(toBig(v)) : (f.valid ?? NON_NEG)(v as number | null);
/**
 * 这一格交不交给 App 存：
 * - 有自己规则的字段（收入 K 的精度、几星一级的整数……）照旧按它的规则：不合规不交——这些规则管的是精度 / 整数，
 *   交出去会被换算取整成另一个能发布的值（1.4 星变 1 星），等于静默改了运营的输入
 * - 默认字段：非负数就交（0 是合法值）；负数、非数字、清空都不交（负数「未填」占位只由系统写入，leo 定）
 * 规则自己接受「空」的字段（收入 K：留空 = 不膨胀），清空照交
 */
const savable = <T,>(f: ParamSpec<T>, v: unknown) => {
  if (f.exact) return validOf(f, v);
  if (f.valid) return f.valid(v as number | null);
  return typeof v === "number" && Number.isFinite(v) && v >= 0;
};

/**
 * 参数表单。骰子参数 / 对抗与控骰 / 竞拍配置都是「一堆带单位的数字」，
 * 三页各写一遍表单布局没有意义，这里按 spec 渲染。
 *
 * 改动不立即生效：本地攒着，点应用才回调。全局参数一改就是全服重算，
 * 边改边生效会让人在半改完的状态下看到错误的派生值。
 *
 * 字段竖排单列而不是两列网格：右侧的派生说明有时很长
 * （「第 50 关时上限 1,250 个」），两列会互相挤。
 */
export function ParamForm<T extends Record<string, number | string | null>>({
  groups, value, editable, onSave, defaults, api, compact = false, extra, placeholders, after,
}: {
  /** 总造价页的经济参数横排，复用同一份校验与自动保存。 */
  compact?: boolean;
  extra?: ReactNode | ((group: ParamGroup<T>) => ReactNode);
  after?: (group: ParamGroup<T>) => ReactNode;
  placeholders?: Partial<Record<keyof T, string>>;
  groups: ParamGroup<T>[];
  value: T;
  editable: boolean;
  onSave: (v: T) => void;
  /**
   * 一套能跑起来的参考值。新代理这一页全是空的红框（新建版本是 -1 占位），
   * 而 E、ρ、双倍概率这种东西测试同学根本不知道该填什么，卡在这儿就没法往下走。
   * 给了就出现「填入参考值」：只补空着的格子，已经填过的一律不动。
   */
  defaults?: T;
  /** 字段 → 接口视角登记 id（lib/apiLensIds.ts）。给了，接口视角打开时每一行挂上字段名 */
  api?: Record<string, string>;
}) {
  const t = useT();
  // 单掷金币版本：按 B 存的数（u.B）界面按「几倍单掷金币」填，换算用旧 E（与副玩法页同一个 GNumInput）
  const scale = useContext(BScaleContext);
  const gUnit = scale.model === 2;
  const allFields = groups.flatMap((g) => g.fields);
  const isBad = (f: ParamSpec<T>, d: T) => f.type !== "datetime" && f.type !== "bool" && !validOf(f, d[f.key]);
  const valueRef = useRef(value);
  valueRef.current = value;
  /*
   * 边改边存，按字段：存得进去的都交给 App（随版本自动保存）——没填、不合规的也照存，发布闸拦（标红提醒发布前要改好）。
   * 只有存不进去的（负数、非数字）留在本地、App 里还是上一次的值
   */
  const { draft, setDraft } = useLiveDraft<T>(value, onSave, {
    enabled: editable,
    toValue: (d) => {
      const out = { ...d };
      for (const f of allFields) if (f.type !== "datetime" && f.type !== "bool" && !savable(f, d[f.key])) out[f.key] = valueRef.current[f.key];
      return out;
    },
  });
  const invalid = allFields.filter((f) => isBad(f, draft));

  return (
    <div className={compact ? `grid grid-cols-1 gap-3 ${groups.length === 2 ? "xl:grid-cols-2" : "xl:grid-cols-3"}` : "flex flex-col gap-page"}>
      {groups.map((g) => (
        <Panel key={g.title}>
          <SectionTitle extra={(typeof extra === "function" ? extra(g) : extra) ?? (g.tag && <Tag tone={g.tag.tone}>{t(g.tag.text)}</Tag>)}>{t(g.title)}</SectionTitle>
          {g.notice && <div className={compact ? "mb-3" : "mb-4"}><Notice tone={g.notice.tone}>{t(g.notice.text)}</Notice></div>}
          {/* 参数名比弹窗里的字段长得多（「每关目标掷骰次数」），标签列单独放宽 */}
          <Form labelWidth={150} className={compact ? "!grid !grid-cols-2 items-start gap-x-4 gap-y-2" : ""}>
            {g.fields.map((f) => {
              const v = draft[f.key] as number | null;
              const bad = isBad(f, draft);
              const prev = valueRef.current[f.key] as Money | null;
              const badText = v == null
                ? (prev != null ? t("common.clearedNotSaved", { v: String(prev) }) : t("common.unfilledPublish"))
                : typeof v === "number" && v < 0 ? t("common.negativeNotSaved")
                : t("common.invalidNotSaved", { v: prev == null ? "—" : String(prev) });
              const note = typeof f.note === "function" ? (f.note as (d: T, tt: Translate) => ReactNode)(draft, t) : f.note ? t(f.note) : null;
              return (
                <Field key={f.key} stacked={compact} label={t(f.label)} tip={f.hint ? t(f.hint) : undefined} api={api?.[f.key]}
                  labelExtra={f.video && <HelpVideo id={f.video} variant="icon" />}>
                  {f.type === "bool" ? (
                    // 值仍是 0 / 1 —— cfg_main_common 是全数字表，勾选框只是它的编辑形态。
                    // 新建版本里是「没设置」（null）：勾选框表达不了第三种状态，旁边写明，勾一下 / 取消一下就算设置了
                    <span className="inline-flex items-center gap-2">
                      <input
                        type="checkbox"
                        className="size-4 accent-primary"
                        checked={Number(v) === 1}
                        disabled={!editable}
                        onChange={(e) => setDraft({ ...draft, [f.key]: e.target.checked ? 1 : 0 })}
                      />
                      {v == null && <span className="text-xs text-danger">{t("common.boolUnset")}</span>}
                    </span>
                  ) : f.type === "datetime" ? (
                    <DateTimeField value={v} disabled={!editable} onChange={(x) => setDraft({ ...draft, [f.key]: x })} />
                  ) : f.exact ? (
                    <NumInput
                      exact
                      value={draft[f.key] as Money | null}
                      placeholder={placeholders?.[f.key]}
                      disabled={!editable}
                      className={`${compact ? "w-[112px]" : "w-[130px]"} ${bad ? "border-danger" : ""}`}
                      onChange={(x) => setDraft({ ...draft, [f.key]: x })}
                    />
                  ) : f.unit === "u.B" && gUnit ? (
                    <GNumInput
                      valueB={typeof v === "number" ? v : null}
                      e={scale.e}
                      disabled={!editable}
                      className={`${compact ? "w-[112px]" : "w-[130px]"} ${bad ? "border-danger" : ""}`}
                      onChangeB={(x) => setDraft({ ...draft, [f.key]: x })}
                    />
                  ) : (
                    <NumInput
                      value={v}
                      placeholder={placeholders?.[f.key]}
                      disabled={!editable}
                      className={`${compact ? "w-[112px]" : "w-[130px]"} ${bad ? "border-danger" : ""}`}
                      onChange={(x) => setDraft({ ...draft, [f.key]: x })}
                    />
                  )}
                  {f.unit && !(f.unit === "u.B" && gUnit) && <span className="flex-none text-sm text-ink-regular">{t(f.unit)}</span>}
                  {f.unit === "u.B" && <BCoins qty={typeof v === "number" ? v : null} />}
                  {bad ? (
                    <span className={`${compact ? "w-full " : ""}text-xs text-danger`}>{badText}</span>
                  ) : (
                    note && <span className={`${compact ? "w-full " : ""}text-xs text-ink-placeholder`}>{note}</span>
                  )}
                </Field>
              );
            })}
            {g.footer && <FieldNote><div className="text-sm text-ink-secondary">{t(g.footer)}</div></FieldNote>}
            {g.bullets && (
              <FieldNote full>
                <ul className="m-0 list-disc pl-[18px] text-sm leading-loose text-ink-regular">
                  {g.bullets.map((b) => <li key={b}>{t(b)}</li>)}
                </ul>
              </FieldNote>
            )}
          </Form>
          {after?.(g)}
        </Panel>
      ))}

      {(!compact || invalid.length > 0) && <div className={`${compact ? "col-span-full " : ""}sticky bottom-0 flex items-center gap-3 rounded-base border border-line-lighter bg-white px-4 py-3`}>
        <span className="text-sm text-ink-placeholder">{t("common.liveSaveHint")}</span>
        {invalid.length > 0 && (
          <span className="text-xs text-danger">{invalid.map((f) => t(f.label)).join("、")} {t("common.invalid")}</span>
        )}
        <span className="ml-auto flex gap-2.5">
          {defaults && editable && (
            <Button
              onClick={() => setDraft((d) => {
                const next = { ...d };
                // 只补空的：已经填过的值不能被「参考值」悄悄改掉
                for (const f of allFields) if (next[f.key] == null) next[f.key] = defaults[f.key];
                return next;
              })}
            >
              {t("common.fillDefaults")}
            </Button>
          )}
        </span>
      </div>}
    </div>
  );
}
